<script>
import get from 'lodash/get';
import DOMPurify from 'dompurify';
import VueSimpleEmbed from '@shared/components/VueSimpleEmbed.vue';
import TeacherSection from '@school/components/TeacherSection.vue';
import RegisterForm from '@school/components/RegisterForm.vue';
import SubscriptionBuyButton from '@school/components/SubscriptionBuyButton.vue';
import TrainingItem from '@school/components/TrainingItem.vue';
import BundleItem from '@school/components/BundleItem.vue';
import APIMemberships from '@school/services/API/Memberships';
import utilsMixin from '@shared/mixins/utils';

export default {
  mixins: [utilsMixin],
  name: 'Membership',
  components: {
    TrainingItem,
    BundleItem,
    VueSimpleEmbed,
    TeacherSection,
    RegisterForm,
    SubscriptionBuyButton,
  },
  created() {
    this.fetchMembership();
  },
  head() {
    if (!this.membership) {
      return {};
    }

    const SEOTitle = get(this.membership, 'seo_data.title');

    return {
      title: SEOTitle || this.membership.name,
      titleTemplate: SEOTitle ? '' : undefined,
    };
  },
  data: () => ({
    isFetching: false,
    membership: null,
  }),
  computed: {
    store() {
      return this.$store.state.store.data;
    },
    authCustomer() {
      return this.$store.getters['auth/customer'];
    },
    membershipForModal() {
      if (!this.membership) return null;

      const prices = this.membership.prices || [];
      if (!prices.length) return null;

      const selectedUuid = this.$route.query.price || prices[0].uuid;

      const selectedPrice = prices.find((price) => price.uuid === selectedUuid) || prices[0];

      // Build membership buyable for checkout modal
      return {
        ...selectedPrice,
        buyable_type: 'MEMBERSHIP_PRICE',
        name: this.membership.name,
        picture: this.membership.picture,
        feature_options: this.membership.feature_options,
        membership: this.membership,
        subscription: {
          ...this.membership,
          prices,
        },
      };
    },
    sanitizedDescription() {
      if (!this.membership?.description) return '';
      return DOMPurify.sanitize(this.membership.description);
    },
    sanitizedGoals() {
      if (!this.membership?.goals) return '';
      return DOMPurify.sanitize(this.membership.goals);
    },
    sanitizedTarget() {
      if (!this.membership?.target) return '';
      return DOMPurify.sanitize(this.membership.target);
    },
    sanitizedRequirements() {
      if (!this.membership?.requirements) return '';
      return DOMPurify.sanitize(this.membership.requirements);
    },
    formattedPrice() {
      if (!this.membership) return '';
      return this.$options.filters.formatPrice(this.membership.price);
    },
    frequencyText() {
      if (!this.membership) return '';
      const lang = this.$i18n?.locale || 'fr';
      return this.formatIntervalPer(this.membership, lang);
    },
  },
  methods: {
    fetchMembership() {
      this.isFetching = true;
      const previewParams = this.$route.query.p ? { p: this.$route.query.p } : undefined;
      APIMemberships.getBySlug(this.$route.params.slug, previewParams)
        .then((response) => {
          this.membership = response.data;
        })
        .catch(() => {
          this.$buefy.notification.open({
            type: 'is-danger',
            message: this.$t('membership_not_exist_or_removed', { storeName: this.store.name }),
          });
          this.$router.push({ name: 'home' });
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
    theme() {
      return this.$t('theme');
    },
  },
  i18n: {
    messages: {
      fr: {
        membership_not_exist_or_removed: "Cet abonnement n'existe pas ou plus sur :storeName",
        accessible_on_pc_mac_mobile: 'Accessible sur PC/Mac et mobile',
        description: 'Description',
        membership_goals: 'Objectifs de l\'abonnement',
        membership_target: 'À qui s\'adresse cet abonnement ?',
        prerequisites: 'Prérequis',
        membership_contents: 'Contenu de cet abonnement',
        loading_problem: 'Un problème de chargement est survenu...',
      },
      en: {
        membership_not_exist_or_removed: "This membership doesn't exist or has been removed from :storeName",
        accessible_on_pc_mac_mobile: 'Accessible on PC/Mac & mobile',
        description: 'Description',
        membership_goals: 'Membership goals',
        membership_target: 'Audience',
        prerequisites: 'Prerequisites',
        membership_contents: 'Included in this membership',
        loading_problem: 'There was a loading problem...',
      },
    },
  },
};
</script>

<template>
  <div class="page">
    <RegisterForm v-if="membership && $route.query.acheter" :buyable="membershipForModal"
      :has-payment-plans="$route.query.options == '1'" @enroll="$router.replace({ name: 'dashboard' })"
      @complete="$router.replace(authCustomer ? { query: {} } : { query: { connexion: 1 } })"
      @close="$routeReferrer ? $router.go(-1) : $router.replace({ query: {} })" />

    <section class="page_banner section is-medium has-background-light is-custom">
      <div class="container">
        <b-skeleton v-if="isFetching" height="40" width="280" />
        <b-skeleton v-if="isFetching" height="40" width="160" />
        <h1 v-else class="title is-custom is-size-4-mobile">
          {{ membership.name }}
        </h1>
      </div>
      <div class="page_info app-header-size-t">
        <div v-if="isFetching" class="box">
          <b-skeleton height="360" />
        </div>
        <div v-else class="box p-0 overflow-hidden">
          <figure v-if="membership.picture" class="image page_img">
            <img :src="`${$env.apiResourcesURL}/${membership.picture}`" :alt="membership.name">
          </figure>
          <div class="p-5 has-text-centered">
            <SubscriptionBuyButton :customer="authCustomer" :subscription="membership" />
          </div>
          <hr class="m-0">
          <ul class="p-5 is-size-8">
            <li>
              <b-icon icon="mobile-alt" type="is-primary is-custom" />
              {{ $t('accessible_on_pc_mac_mobile') }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="page_content section is-medium">
      <div v-if="isFetching" class="container">
        <b-skeleton height="400" />
      </div>
      <div v-else class="container">
        <figure v-if="membership.promotional_video_url" class="has-background-black mb-5">
          <VueSimpleEmbed :attrs="{
            class: 'w-full',
            width: '792',
            height: '445.50'
          }" :url="membership.promotional_video_url">
            <b-notification type="is-danger" :closable="false">
              {{ $t('loading_problem') }}
            </b-notification>
          </VueSimpleEmbed>
        </figure>
        <article v-if="membership.description" class="mb-10">
          <h2 class="title is-custom is-4 is-size-5-touch mb-3">
            {{ $t('description') }}
          </h2>
          <div class="content" v-html="sanitizedDescription" />
        </article>
        <article v-if="membership.goals" class="mb-10">
          <h2 class="title is-custom is-4 is-size-5-touch mb-3">
            {{ $t('membership_goals') }}
          </h2>
          <div class="content" v-html="sanitizedGoals" />
        </article>
        <article v-if="membership.target" class="mb-10">
          <h2 class="title is-custom is-4 is-size-5-touch mb-3">
            {{ $t('membership_target') }}
          </h2>
          <div class="content" v-html="sanitizedTarget" />
        </article>
        <article v-if="membership.requirements" class="mb-10">
          <h2 class="title is-custom is-4 is-size-5-touch mb-3">
            {{ $t('prerequisites') }}
          </h2>
          <div class="content" v-html="sanitizedRequirements" />
        </article>
        <article v-if="membership.items && membership.items.length > 0">
          <h2 class="title is-custom is-4 is-size-5-touch mb-3">
            {{ $t('membership_contents') }}
          </h2>

          <div class="my-4">
            <div v-for="item in membership.items" :key="item.uuid" class="mb-4">
              <TrainingItem
                v-if="item.buyable_type === 'TRAINING'"
                :training="item.buyable"
                :has-price="false"
                :is-buyable="false"
              />
              <BundleItem
                v-else-if="item.buyable_type === 'BUNDLE'"
                :bundle="item.buyable"
                :has-price="false"
                :is-embed="false"
              />
            </div>
          </div>
        </article>
      </div>
    </section>

    <TeacherSection class="page_teacher" :buyable="membership" />
  </div>
</template>

<style lang="scss" scoped>
$infosize: 320px;

.page {

  &_banner .container,
  &_content .container,
  &_teacher ::v-deep .container {
    padding-right: $infosize + 40;
  }

  &_img {
    max-height: 320px;

    img {
      object-fit: cover;
      object-position: center top;
    }
  }

  &_info {
    position: fixed;
    z-index: 10;
    left: 50%;
    transform: translateX($infosize / 2);
    width: 320px;
    margin-top: 20px;

    @include widescreen {
      $container-offset: (
        2 * $gap
      );
    $container-max-width: $fullhd;
    $contaier-width: min($widescreen, $container-max-width) - $container-offset;
    transform: translateX(($contaier-width / 2) - 320);
  }
}

@include touch {

  &_banner .container,
  &_content .container,
  &_teacher ::v-deep .container {
    padding-right: $gap;
  }

  &_info {
    position: static;
    transform: none;
    width: auto;
    margin-top: 0;
  }
}
}
</style>
